'use client';

import { cn } from '@/utils/styles';
import { liveUpdateAttrs } from '@/libs/contentful/utils';

import CTA from '@/components/shared/CTA/CTA';
import Media from '@/components/shared/Media/Media';
import RichTextCopy from '@/components/shared/RichTextCopy/RichTextCopy';
import RichTextHeadline from '@/components/shared/RichTextHeadline/RichTextHeadline';

import { RemoveCircle, AddCircle } from '@1debit/icons';
import { CardsVerticalScrollItemCovariantProps } from '../types';
import { CopyJustification } from '@/types/constants';
import { getRichTextValue } from '@/utils/formatters';
import { getBulletListStyle } from '@/utils/bulletListHelpers';

const DesktopCardsVerticalScrollItem = ({
  sys,
  headline,
  copy,
  desktopMedia,
  mobileMedia,
  cta,
  copyAlignment,
  bulletStyle,
  isExpanded,
  onExpand,
}: CardsVerticalScrollItemCovariantProps) => {
  const plainHeadline = getRichTextValue(headline);

  const internalClassNames = {
    wrapper: cn('hidden sm:flex pl-20 flex-col gap-8'),
    header: {
      wrapper: cn('w-full flex justify-between p-5 pl-0 items-center'),
      headline: cn('font-sans font-semibold text-[1.75rem] font-[570] leading-[1.25]'),
      collapseButton: cn('flex items-center rounded-full cursor-pointer'),
    },

    innerContent: {
      wrapper: cn('grid grid-cols-2 md:grid-cols-[25rem_1fr] gap-6 md:gap-12 justify-between transition-all', {
        hidden: !isExpanded,
      }),

      left: {
        wrapper: cn('flex flex-col gap-4 mt-20', {
          'items-center text-center': copyAlignment === CopyJustification.Center,
          'items-end text-right': copyAlignment === CopyJustification.Right,
        }),
        headline: cn('font-sans font-[500] text-heading-lg tracking-[calc(-3vw/100)]'),
        copy: cn('font-serif text-body-serif text-md pr-4 leading-[140%]'),
        cta: cn('w-4/6 !max-w-[unset] mt-4'),
      },

      right: {
        media: cn('aspect-16/11 rounded-bl-none'),
      },
    },
  };

  const InnerComponent = () => (
    <div className={internalClassNames.innerContent.wrapper} data-testid="card-desktop-content">
      <div className={internalClassNames.innerContent.left.wrapper}>
        <div
          className={internalClassNames.innerContent.left.headline}
          {...liveUpdateAttrs(sys?.id, 'headline')}
          data-testid="card-expanded-headline">
          <RichTextHeadline headline={headline} />
        </div>

        <div
          className={internalClassNames.innerContent.left.copy}
          {...liveUpdateAttrs(sys?.id, 'copy')}
          data-testid="card-expanded-copy">
          <RichTextCopy copy={copy} bulletListStyle={getBulletListStyle(bulletStyle)} />
        </div>
        {cta && <CTA cta={cta} variant="outline" className={internalClassNames.innerContent.left.cta} />}
      </div>
      {desktopMedia && (
        <Media
          media={desktopMedia}
          mobileMedia={mobileMedia}
          sizes="(max-width: 751px) 100vw, (max-width: 1440px) 50vw, 760px"
          className={internalClassNames.innerContent.right.media}
        />
      )}
    </div>
  );

  return (
    <div className={internalClassNames.wrapper}>
      <div className={internalClassNames.header.wrapper}>
        <div
          className={internalClassNames.header.headline}
          {...liveUpdateAttrs(sys?.id, 'headline')}
          data-testid="card-collapsed-headline">
          <RichTextHeadline headline={headline} />
        </div>
        <button
          data-testid="desktop-vertical-card-control"
          className={internalClassNames.header.collapseButton}
          aria-controls={`${sys?.id}-content`}
          aria-expanded={isExpanded}
          aria-label={`Expand/Collapse ${plainHeadline} Card`}
          onClick={onExpand}>
          {isExpanded ? <RemoveCircle className="w-8 h-8" /> : <AddCircle className="w-8 h-8" />}
        </button>
      </div>

      <InnerComponent />
    </div>
  );
};

export default DesktopCardsVerticalScrollItem;
